import type { ComponentProps, ReactNode } from 'react'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'

export type SPContentBaseActionButtonProps = Omit<ComponentProps<typeof TextButton>, 'kind' | 'variant' | 'shape' | 'size' | 'children'>

type SPContentBaseActionProps = SPContentBaseActionButtonProps & Pick<ComponentProps<typeof TextButton>, 'size' | 'fit' | 'children'>

interface Props extends Utils.PropsWithDataAttributes {
  className?: string
  heading: ReactNode
  body?: ReactNode
  headingAs?: ComponentProps<typeof Heading>['as']
  /**
   * Optional CTA label matching Global Kit Button label. Omit to hide the button.
   */
  buttonLabel?: ReactNode
  /**
   * Extra TextButton props for the CTA. Does not include the label; use `buttonLabel`.
   */
  actionButtonProps?: SPContentBaseActionButtonProps
}

export function SPContentBaseAction({
  fit = 'auto',
  size = 'medium',
  ...props
}: SPContentBaseActionProps) {
  return <TextButton
    kind="secondary"
    variant="default"
    shape="round"
    {...props}
    size={size}
    fit={fit}
  />
}

function SPContentBase(props: Props) {
  const {
    className,
    heading,
    body,
    headingAs = 'h2',
    buttonLabel,
    actionButtonProps,
    ...rest
  } = props

  return <Group
    {...rest}
    className={className}
    direction="vertical"
    gap={16}
    fullWidth
    horizontalAlign="start"
  >
    <Group
      direction="vertical"
      gap={8}
      fullWidth
      horizontalAlign="start"
    >
      <Heading
        variant="heading3"
        as={headingAs}
        wrap="word-wrap"
      >
        {heading}
      </Heading>
      {!!body && <BodyText
        variant="large"
        colour="light-foreground-alt"
        wrap="word-wrap"
      >
        {body}
      </BodyText>}
    </Group>
    {!!buttonLabel && <SPContentBaseAction {...actionButtonProps}>
      {buttonLabel}
    </SPContentBaseAction>}
  </Group>
}

export default SPContentBase
